import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import templateService from "../../../services/template.service";
import Loading from "../../common/Loading";
import Swal from '../../helpers/swal';
const CreateAdminCallTemplate = () => {
    const navigate = useNavigate();
    const [templateTypes, setTemplateTypes] = useState([]);
    const [existingCallTemplates, setExistingCallTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingEcx, setLoadingEcx] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        reminderName: '',
        script: '',
        usedBy: 'CLINIC'
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setLoadingEcx(true);

                // Fetch both template types and existing Call templates in parallel
                const [templateTypesResponse, ecxTemplatesResponse] = await Promise.all([
                    templateService.getTemplateTypes(),
                    templateService.getEcxTemplate(0, 1000) // Fetch all templates
                ]);

                setTemplateTypes(templateTypesResponse.data);
                setExistingCallTemplates(ecxTemplatesResponse.data.content);
            } catch (error) {
                console.error('Error fetching data:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load template data. Please try again.',
                    confirmButtonColor: '#dc3545'
                });
            } finally {
                setLoading(false);
                setLoadingEcx(false);
            }
        };

        fetchData();
    }, []);

    const handleTemplateChange = (selectedReminderName) => {
        const selectedTemplate = templateTypes.find(template => template.reminderName === selectedReminderName);
        if (selectedTemplate) {
            setFormData({
                ...formData,
                reminderName: selectedReminderName,
                usedBy: selectedTemplate.usedBy
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSubmitting(true);

            // Find the selected template to get the reminderTypeId
            const selectedTemplate = templateTypes.find(t => t.reminderName === formData.reminderName);

            if (!selectedTemplate) {
                throw new Error('Selected template not found');
            }

            // Prepare the request body
            const requestBody = {
                reminderName: formData.reminderName,
                reminderTypeId: selectedTemplate.id,
                mediumType: "CALL",
                content: formData.script, // Using script field as content for call templates
                usedBy: formData.usedBy
            };

            // Call the save API
            const response = await templateService.saveEcxTemplate(requestBody);

            // Show success message
            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Call template created successfully!',
                confirmButtonColor: '#0dcaf0',
                timer: 2000,
                showConfirmButton: true
            });

            // Redirect to templates list
            navigate('/admin-templates');

        } catch (error) {
            console.error('Error creating Call template:', error);

            // Show error message
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to create Call template. Please try again.',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'You will lose all unsaved changes.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, cancel!`,
            cancelButtonText: `<i class="bi bi-x"></i> No Keep editing`,
            reverseButtons: true,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-danger me-2 btn-sm',
                cancelButton: 'btn btn-secondary me-2 btn-sm'
            },
            buttonsStyling: false
        }).then((result) => {
            if (result.isConfirmed) {
                navigate('/admin-templates');
            }
        });
    };

    // Filter templates that support Call and don't have existing Call templates
    const availableCallTemplates = templateTypes.filter(template => {
        // Check if template supports Call
        if (!template.isCall) return false;

        // Check if this template already has a Call template created
        const alreadyExists = existingCallTemplates.some(existingTemplate =>
            existingTemplate.reminderTypeId === template.id &&
            existingTemplate.mediumType === 'CALL'
        );

        return !alreadyExists;
    });

    // Get already created Call templates for information display
    const createdCallTemplates = templateTypes.filter(template => {
        return existingCallTemplates.some(existingTemplate =>
            existingTemplate.reminderTypeId === template.id &&
            existingTemplate.mediumType === 'CALL'
        );
    });

    return (
        <div className="container container-xxl">
            <div className="pagetitle">
                <h1><i className="bi bi-telephone text-info me-2"></i> Create Call Template</h1>
            </div>

            <div className="card card-padding">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row mb-3 mt-3">
                            <div className="col-md-6">
                                <label className="form-label">Template Name *</label>
                                {loading || loadingEcx ? (
                                    <Loading/>
                                ) : (
                                    <>
                                        <select
                                            className="form-control"
                                            value={formData.reminderName}
                                            onChange={(e) => handleTemplateChange(e.target.value)}
                                            required
                                        >
                                            <option value="">Select a template type</option>
                                            {availableCallTemplates.map(template => (
                                                <option key={template.id} value={template.reminderName}>
                                                    {template.reminderName}
                                                </option>
                                            ))}
                                        </select>
                                        {availableCallTemplates.length === 0 && (
                                            <div className="alert alert-warning mt-2 mb-0">
                                                <i className="bi bi-exclamation-triangle me-2"></i>
                                                All available Call templates have already been created.
                                            </div>
                                        )}
                                    </>
                                )}
                                <div className="form-text">
                                    Select a template type that supports Call and hasn't been created yet
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Used By *</label>
                                <select
                                    className="form-control"
                                    value={formData.usedBy}
                                    onChange={(e) => setFormData({...formData, usedBy: e.target.value})}
                                    required
                                    disabled // Make it read-only since it's auto-set
                                >
                                    <option value="CLINIC">Clinic</option>
                                    <option value="ECX">ECX</option>
                                </select>
                                <div className="form-text">
                                    Automatically set based on template selection
                                </div>
                            </div>
                        </div>

                        {createdCallTemplates.length > 0 && (
                            <div className="alert alert-info mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <i className="bi bi-check-circle me-2"></i>
                                    <h6 className="mb-0">Already Created Call Templates</h6>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    {createdCallTemplates.map(template => (
                                        <span key={template.id} className="badge bg-secondary">
                                            {template.reminderName}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {formData.reminderName && (
                            <div className="alert alert-info mb-3">
                                <div className="d-flex align-items-center mb-2">
                                    <i className="bi bi-info-circle me-2 fs-5"></i>
                                    <h6 className="mb-0">Template Information</h6>
                                </div>

                                <div className="row">
                                    <div className="col-md-3">
                                        <div className="template-info-item">
                                            <span className="fw-semibold text-dark">Name:</span>
                                            <span className="ms-1">{formData.reminderName}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="template-info-item">
                                            <span className="fw-semibold text-dark">Category:</span>
                                            <span className="ms-1">
                                                {templateTypes.find(t => t.reminderName === formData.reminderName)?.rootCategory || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="template-info-item">
                                            <span className="fw-semibold text-dark">Priority:</span>
                                            <span className="ms-1">
                                                {templateTypes.find(t => t.reminderName === formData.reminderName)?.priority || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="template-info-item">
                                            <span className="fw-semibold text-dark">ID:</span>
                                            <span className="ms-1 small font-monospace">
                                                {templateTypes.find(t => t.reminderName === formData.reminderName)?.id || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="form-label">Call Script *</label>
                            <textarea
                                className="form-control"
                                rows="6"
                                value={formData.script}
                                onChange={(e) => setFormData({...formData, script: e.target.value})}
                                placeholder="Enter the call script for the operator"
                                required
                            />
                            <div className="form-text">
                                Available variables: {"{PATIENT_NAME}, {CLINIC_NAME}, {APPOINTMENT_DATE}, {APPOINTMENT_TIME}"}
                            </div>
                        </div>

                        <div className="d-flex gap-2 justify-content-end">
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={handleCancel}
                                disabled={submitting}
                            >
                                <i className="bi bi-x"></i> Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-sm btn-info text-white"
                                disabled={!formData.reminderName || availableCallTemplates.length === 0 || submitting}
                            >
                                {submitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-floppy"></i> Create Call Template
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateAdminCallTemplate;